// src/pages/BookingAppointment.jsx - FIXED VERSION with Dynamic Typography
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { getSalonConfig } from '../api/salonConfigService';
import { useSalonLocations } from '../hooks/useSalonLocations';
import {
  collection,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { canShareSlot } from '../utils/multiServiceAvailability';
import '../styles/booking.css';
// #Revised UI_v2 — CE3: react-datepicker for date selection
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Helper function to convert font size to CSS value
const getFontSizeValue = (fontSize) => {
  switch (fontSize) {
    case 'small':
      return '14px';
    case 'large':
      return '18px';
    case 'medium':
    default:
      return '16px';
  }
};

function BookingAppointment() {
  const { salonId: _rawSalonId } = useParams();
  const salonId = _rawSalonId?.toLowerCase();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useCustomerAuth();

  // Check if this is a reschedule operation
  const isReschedule = location.state?.isReschedule || false;
  const originalAppointment = location.state?.originalAppointment || null;
  const rescheduleData = location.state?.rescheduleData || null;

  // Pre-selected package (navigated from CustomerPackages page)
  const preselectedPackage = location.state?.preselectedPackage || null;

  // Location
  const { locations } = useSalonLocations(salonId);
  const isMultiLocation = locations.length > 1;
  const [selectedLocation, setSelectedLocation] = useState('');

  // When locations load, default to first if not yet set
  useEffect(() => {
    if (isMultiLocation && !selectedLocation && locations.length > 0) {
      setSelectedLocation(locations[0].locationId);
    }
  }, [isMultiLocation, locations]);

  // Form state
  // Pre-selected service (navigated from a service card's "Book Now" button)
  const preselectedService = location.state?.selectedService || null;
  const [selectedService, setSelectedService] = useState(preselectedService?.id || '');
  const [selectedStaff, setSelectedStaff] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedPackageId, setSelectedPackageId] = useState(preselectedPackage?.id || '');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Data state
  const [allServices, setAllServices] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [businessHours, setBusinessHours] = useState([]);
  const [holidayClosures, setHolidayClosures] = useState([]);
  const [customClosures, setCustomClosures] = useState([]);
  const [salonConfig, setSalonConfig] = useState(null);
  const [customerActivePackages, setCustomerActivePackages] = useState([]);

  // Validation state
  const [formErrors, setFormErrors] = useState({});

  // Scroll to top on mount
  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Load active packages for the logged-in customer
  useEffect(() => {
    if (!currentUser?.email || !salonId) return;
    const customerId = `${currentUser.email}_${salonId}`;
    const q = query(
      collection(db, 'customerPackages'),
      where('customerId', '==', customerId),
      where('salonId', '==', salonId),
      where('status', '==', 'active')
    );
    getDocs(q).then(snap => {
      setCustomerActivePackages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }).catch(() => {});
  }, [currentUser, salonId]);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError('');

        console.log('Loading data for salon ID:', salonId);

        // Load salon configuration for business hours AND typography
        const salonConfigData = await getSalonConfig(salonId);
        if (salonConfigData) {
          setSalonConfig(salonConfigData);

          // Extract business hours - handle both old and new formats
          // For multi-location, prefer the selected location's hours if available
          let hoursArray = [];
          let holidays = [];
          let custom = [];
          if (salonConfigData.businessHours) {
            const bh = salonConfigData.businessHours;
            // Try per-location hours first when selectedLocation is set
            const locHours = selectedLocation && bh.locations?.[selectedLocation];
            if (locHours) {
              hoursArray = locHours.regularHours || [];
              custom = locHours.customClosures || [];
            } else if (Array.isArray(bh)) {
              hoursArray = bh;
            } else {
              hoursArray = bh.regularHours || [];
              custom = bh.customClosures || [];
            }
            // Holidays are always org-wide
            holidays = bh.holidayClosures || [];
          }
          setBusinessHours(hoursArray);
          setHolidayClosures(holidays);
          setCustomClosures(custom);
          console.log('Holiday closures loaded:', holidays);
          console.log('Custom closures loaded:', custom);
        }

        // Load services from flat hierarchy collection
        try {
          const servicesRef = collection(db, 'services');
          const servicesQuery = query(servicesRef, where('salonId', '==', salonId));
          const servicesSnapshot = await getDocs(servicesQuery);
          const servicesData = servicesSnapshot.docs
            .map(doc => ({
              id: doc.id,
              ...doc.data()
            }))
            // Filter client-side for active services (isActive === true or isActive is undefined)
            .filter(service => service.isActive !== false);

          console.log('Loaded services from flat hierarchy collection:', servicesData);
          setAllServices(servicesData);
        } catch (servicesError) {
          console.error('Error loading services from flat hierarchy collection:', servicesError);
          setError('Failed to load services. Please try again.');
          return;
        }

        // Load staff from flat hierarchy collection
        try {
          const staffRef = collection(db, 'staff');
          const staffQuery = query(staffRef, where('salonId', '==', salonId));
          const staffSnapshot = await getDocs(staffQuery);
          const staffData = staffSnapshot.docs
            .map(doc => ({
              id: doc.id,
              ...doc.data()
            }))
            // Filter client-side for active staff (isActive === true or isActive is undefined)
            .filter(staff => staff.isActive !== false);

          console.log('Loaded staff from flat hierarchy collection:', staffData);
          setAllStaff(staffData);
          // Initially show all staff
          setFilteredStaff(staffData);
        } catch (staffError) {
          console.error('Error loading staff from flat hierarchy collection:', staffError);
          setError('Failed to load staff. Please try again.');
          return;
        }

        // Pre-fill form if this is a reschedule
        if (isReschedule && rescheduleData) {
          console.log('Pre-filling form for reschedule:', rescheduleData);
          
          // Set service (after services are loaded)
          if (rescheduleData.serviceId || rescheduleData.serviceName) {
            setTimeout(() => {
              setSelectedService(rescheduleData.serviceId || rescheduleData.serviceName);
            }, 100);
          }

          // Set staff (after staff are loaded)
          if (rescheduleData.staffId || rescheduleData.staffName) {
            setTimeout(() => {
              setSelectedStaff(rescheduleData.staffId || rescheduleData.staffName);
            }, 100);
          }

          // Set original date and time as default
          if (rescheduleData.originalDateTime) {
            const originalDate = new Date(rescheduleData.originalDateTime);
            setSelectedDate(originalDate.toISOString().split('T')[0]);
            
            const hours = originalDate.getHours().toString().padStart(2, '0');
            const minutes = originalDate.getMinutes().toString().padStart(2, '0');
            setSelectedTime(`${hours}:${minutes}`);
          }

          // Set notes
          if (rescheduleData.notes) {
            setNotes(rescheduleData.notes);
          }
        }

      } catch (error) {
        console.error('Error loading booking data:', error);
        setError('Failed to load booking information. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (salonId) {
      loadData();
    } else {
      setError('Salon ID is missing');
      setLoading(false);
    }
  }, [salonId, isReschedule, rescheduleData, selectedLocation]);

  // Set salon colors AND typography as CSS variables
  useEffect(() => {
    if (salonConfig?.branding) {
      const root = document.documentElement;
      
      // Apply colors
      if (salonConfig.branding.colors?.primary) {
        const primaryColor = salonConfig.branding.colors.primary;
        root.style.setProperty('--salon-primary-color', primaryColor);
        
        // Convert hex to RGB for rgba usage in box-shadows
        if (primaryColor.startsWith('#')) {
          const r = parseInt(primaryColor.slice(1, 3), 16);
          const g = parseInt(primaryColor.slice(3, 5), 16);
          const b = parseInt(primaryColor.slice(5, 7), 16);
          root.style.setProperty('--salon-primary-color-rgb', `${r}, ${g}, ${b}`);
        }
      }
      
      // Apply typography
      if (salonConfig.branding.typography) {
        root.style.setProperty('--salon-heading-font', salonConfig.branding.typography.headingFont || 'Familjen Grotesk, sans-serif');
        root.style.setProperty('--salon-body-font', salonConfig.branding.typography.bodyFont || 'Wix Madefor Text, sans-serif');
        root.style.setProperty('--salon-font-size', getFontSizeValue(salonConfig.branding.typography.fontSize));
      }
    }
  }, [salonConfig]);

  // Staff at the selected location (backward-compatible: no locationIds → show everywhere)
  const locationFilteredStaff = allStaff.filter(s =>
    !selectedLocation ||
    (s.locationIds && s.locationIds.includes(selectedLocation)) ||
    s.locationId === selectedLocation ||
    (!s.locationIds && !s.locationId)
  );

  // Services available at the selected location = union of services[] across all
  // staff at that location. This replaces the old service.locationId approach so
  // a service appears for customers only if at least one stylist at that branch offers it.
  const locationFilteredServices = (() => {
    if (!selectedLocation) return allServices;
    const serviceIdSet = new Set();
    locationFilteredStaff.forEach(s =>
      (s.services || []).forEach(id => serviceIdSet.add(id))
    );
    // Match on both Firestore doc id and legacy serviceId UUID field
    return allServices.filter(sv =>
      serviceIdSet.has(sv.id) || serviceIdSet.has(sv.serviceId)
    );
  })();

  // Filter staff based on selected service (applied on top of location filter)
  useEffect(() => {
    if (selectedService && locationFilteredStaff.length > 0) {
      const selectedServiceData = locationFilteredServices.find(s => s.id === selectedService);
      if (selectedServiceData) {
        const availableStaff = locationFilteredStaff.filter(staff =>
          staff.services && (
            staff.services.includes(selectedServiceData.id) ||
            (selectedServiceData.serviceId && staff.services.includes(selectedServiceData.serviceId))
          )
        );

        console.log(`Filtering staff for service "${selectedServiceData.name}":`, availableStaff);
        setFilteredStaff(availableStaff);

        if (selectedStaff) {
          const isCurrentStaffAvailable = availableStaff.some(s => s.id === selectedStaff);
          if (!isCurrentStaffAvailable) {
            setSelectedStaff('');
          }
        }
      }
    } else {
      setFilteredStaff(locationFilteredStaff);
    }
  }, [selectedService, allStaff, allServices, selectedStaff, selectedLocation]);

  // Generate time slots with availability status
  useEffect(() => {
    if (selectedDate && selectedStaff && businessHours.length > 0) {
      generateTimeSlots();
    } else {
      setAvailableTimeSlots([]);
    }
  }, [selectedDate, selectedStaff, businessHours]);

  const generateTimeSlots = async () => {
    if (!selectedDate || !selectedStaff) return;

    try {
      const selectedDayName = new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' });
      const dayHours = businessHours.find(day => day.day === selectedDayName);

      // If salon is closed on this day (regular hours), show no slots
      if (!dayHours || dayHours.isClosed) {
        setAvailableTimeSlots([]);
        return;
      }

      // Check if salon is closed due to a holiday closure
      const holidayMatch = holidayClosures.find(h => h && h.date === selectedDate);
      if (holidayMatch) {
        console.log('Salon closed for holiday:', holidayMatch.name);
        setAvailableTimeSlots([]);
        return;
      }

      // Check if salon is closed due to a custom closure (full day)
      const selectedDateObj = new Date(selectedDate + 'T00:00:00');
      selectedDateObj.setHours(0, 0, 0, 0);
      const fullDayClosure = customClosures.find(closure => {
        if (!closure || !closure.startDate || !closure.endDate) return false;
        const startDate = new Date(closure.startDate);
        const endDate = new Date(closure.endDate);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        return selectedDateObj >= startDate && selectedDateObj <= endDate && closure.isFullDay;
      });
      if (fullDayClosure) {
        console.log('Salon closed for custom closure:', fullDayClosure.reason);
        setAvailableTimeSlots([]);
        return;
      }

      // Collect partial custom closures for this date (used per-slot below)
      const partialClosures = customClosures.filter(closure => {
        if (!closure || !closure.startDate || !closure.endDate || closure.isFullDay) return false;
        const startDate = new Date(closure.startDate);
        const endDate = new Date(closure.endDate);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        return selectedDateObj >= startDate && selectedDateObj <= endDate && closure.startTime && closure.endTime;
      });

      // Check if staff is on leave for the selected date
      const leaveInfo = await checkStaffLeave(selectedStaff, selectedDate);

      // If staff is on full day leave, show no slots
      if (leaveInfo.isOnLeave && leaveInfo.timeType === 'full') {
        setAvailableTimeSlots([]);
        return;
      }

      // Get existing appointments for the selected staff and date
      const existingAppointments = await getExistingAppointments(selectedStaff, selectedDate);
      console.log('Existing appointments for slot check:', existingAppointments);

      // Get staff working hours
      const staffWH = await getStaffWorkingHours(selectedStaff);
      console.log('Staff working hours data:', JSON.stringify(staffWH));
      console.log('Employment type:', staffWH.employmentType);
      console.log('Roster keys:', Object.keys(staffWH.roster || {}));
      if (staffWH.roster && staffWH.roster[selectedDate]) {
        console.log(`Roster for ${selectedDate}:`, staffWH.roster[selectedDate]);
      } else {
        console.log(`No roster entry for ${selectedDate}`);
      }

      // Generate base time slots from business hours
      const [openHour, openMinute] = dayHours.open.split(':').map(Number);
      const [closeHour, closeMinute] = dayHours.close.split(':').map(Number);

      const startTime = openHour * 60 + openMinute; // Convert to minutes
      const endTime = closeHour * 60 + closeMinute;

      const allSlots = [];

      // Generate 30-minute slots
      for (let time = startTime; time < endTime; time += 30) {
        const hour = Math.floor(time / 60);
        const minute = time % 60;
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

        // Check partial custom closures for this specific slot
        const partialClosure = partialClosures.find(closure => {
          const closureStartMinutes = timeStringToMinutes(closure.startTime);
          const closureEndMinutes = timeStringToMinutes(closure.endTime);
          return time >= closureStartMinutes && time < closureEndMinutes;
        });
        if (partialClosure) {
          allSlots.push({
            value: timeString,
            label: formatTimeDisplay(timeString),
            timeInMinutes: time,
            available: false,
            reason: `Salon closed: ${partialClosure.reason || 'Custom closure'}`,
            disabled: true
          });
          continue;
        }

        // Check working hours first
        const whStatus = checkWorkingHoursAvailability(staffWH, selectedDate, timeString);
        if (!whStatus.available) {
          allSlots.push({
            value: timeString,
            label: formatTimeDisplay(timeString),
            timeInMinutes: time,
            available: false,
            reason: whStatus.reason,
            disabled: true
          });
          continue;
        }

        // Check availability for this specific time slot (leave & appointments)
        const slotStatus = checkSlotAvailability(time, existingAppointments, leaveInfo);

        allSlots.push({
          value: timeString,
          label: formatTimeDisplay(timeString),
          timeInMinutes: time,
          available: slotStatus.available,
          reason: slotStatus.reason,
          disabled: !slotStatus.available
        });
      }

      console.log(`Time slots for ${selectedDate}:`, allSlots);
      setAvailableTimeSlots(allSlots);

    } catch (error) {
      console.error('Error generating time slots:', error);
      setAvailableTimeSlots([]);
    }
  };

  const checkSlotAvailability = (slotTimeInMinutes, existingAppointments, leaveInfo) => {
    // Check if this slot conflicts with partial leave
    if (leaveInfo.isOnLeave && leaveInfo.timeType === 'partial' && leaveInfo.leaveRecords) {
      for (const leave of leaveInfo.leaveRecords) {
        if (leave.timeType === 'am') {
          // AM leave: 9:00 - 13:00 (540 - 780 minutes) per StaffLeaveManager definition
          if (slotTimeInMinutes >= 540 && slotTimeInMinutes < 780) {
            return { available: false, reason: 'Staff on leave (AM)' };
          }
        } else if (leave.timeType === 'pm') {
          // PM leave: 13:00 - 18:00 (780 - 1080 minutes)
          if (slotTimeInMinutes >= 780 && slotTimeInMinutes < 1080) {
            return { available: false, reason: 'Staff on leave (PM)' };
          }
        } else if (leave.timeType === 'custom' && leave.startTime && leave.endTime) {
          // Custom time range
          const leaveStartMinutes = timeStringToMinutes(leave.startTime);
          const leaveEndMinutes = timeStringToMinutes(leave.endTime);
          if (slotTimeInMinutes >= leaveStartMinutes && slotTimeInMinutes < leaveEndMinutes) {
            return { available: false, reason: 'Staff on leave' };
          }
        }
      }
    }

    // Check if slot conflicts with existing appointments
    const conflictingAppointments = existingAppointments.filter(appointment => {
      if (!appointment.startTime || !appointment.endTime) return false;
      const appointmentStartMinutes = timeStringToMinutes(appointment.startTime);
      const appointmentEndMinutes = timeStringToMinutes(appointment.endTime);

      // Does this slot fall within an existing appointment?
      return slotTimeInMinutes >= appointmentStartMinutes && slotTimeInMinutes < appointmentEndMinutes;
    });

    if (conflictingAppointments.length > 0) {
      const staffObj = allStaff.find(s => s.id === selectedStaff);
      const requestedSvc = allServices.find(s => s.id === selectedService);
      const overlappingServices = conflictingAppointments
        .map(appointment => allServices.find(s => s.id === appointment.serviceId))
        .filter(Boolean);

      if (!canShareSlot(staffObj, overlappingServices, requestedSvc)) {
        return {
          available: false,
          reason: 'Already booked'
        };
      }
    }

    // Slot is available
    return {
      available: true,
      reason: null
    };
  };

  const checkStaffLeave = async (staffId, date) => {
    try {
      const leaveRef = collection(db, 'leave');
      const leaveQuery = query(
        leaveRef,
        where('staffId', '==', staffId),
        where('salonId', '==', salonId)
      );
      const leaveSnapshot = await getDocs(leaveQuery);

      console.log(`Leave records found for staff ${staffId}:`, leaveSnapshot.size);

      const selectedDate = new Date(date + 'T00:00:00');
      selectedDate.setHours(0, 0, 0, 0);

      // Collect all leave records affecting this date
      const leaveRecords = [];
      for (const docSnap of leaveSnapshot.docs) {
        const leaveData = docSnap.data();
        const startDate = leaveData.startDate?.toDate ? leaveData.startDate.toDate() : new Date(leaveData.startDate);
        const endDate = leaveData.endDate?.toDate ? leaveData.endDate.toDate() : new Date(leaveData.endDate);

        const startDateNorm = new Date(startDate);
        startDateNorm.setHours(0, 0, 0, 0);
        const endDateNorm = new Date(endDate);
        endDateNorm.setHours(23, 59, 59, 999);

        // Check if selected date falls within leave period
        if (selectedDate >= startDateNorm && selectedDate <= endDateNorm) {
          console.log('Leave match found:', leaveData.type, leaveData.timeType, leaveData.startTime, leaveData.endTime);
          leaveRecords.push({
            timeType: leaveData.timeType || 'full',
            reason: leaveData.reason || 'On leave',
            type: leaveData.type || 'leave',
            startTime: leaveData.startTime || null,
            endTime: leaveData.endTime || null
          });
        }
      }

      if (leaveRecords.length === 0) {
        return {
          isOnLeave: false,
          timeType: null,
          reason: null,
          type: null,
          leaveRecords: []
        };
      }

      // Check if any leave is full day
      const fullDayLeave = leaveRecords.find(l => l.timeType === 'full');
      if (fullDayLeave) {
        return {
          isOnLeave: true,
          timeType: 'full',
          reason: fullDayLeave.reason,
          type: fullDayLeave.type,
          leaveRecords: leaveRecords
        };
      }

      // Return all partial leave records for time-specific checking
      return {
        isOnLeave: true,
        timeType: 'partial',
        reason: leaveRecords[0].reason,
        type: leaveRecords[0].type,
        leaveRecords: leaveRecords
      };
    } catch (error) {
      console.error('Error checking staff leave:', error);
      return {
        isOnLeave: false,
        timeType: null,
        reason: null,
        type: null,
        leaveRecords: []
      };
    }
  };

  const getStaffWorkingHours = async (staffId) => {
    try {
      const whRef = collection(db, 'workingHours');

      // For multi-location salons, prefer the location-specific working hours record.
      // Fall back to any record for this staff if no location-specific one exists.
      if (selectedLocation) {
        const locQuery = query(
          whRef,
          where('staffId', '==', staffId),
          where('salonId', '==', salonId),
          where('locationId', '==', selectedLocation)
        );
        const locSnapshot = await getDocs(locQuery);

        if (!locSnapshot.empty) {
          const data = locSnapshot.docs[0].data();
          const rawOffDay = data.offDay;
          return {
            employmentType: data.employmentType || 'fulltime',
            roster: data.roster || {},
            offDays: Array.isArray(rawOffDay) ? rawOffDay : (rawOffDay ? [rawOffDay] : []),
          };
        }
        // No location-specific record — fall through to general query below
      }

      // General query (single-location salons or no location-specific record found)
      const whQuery = query(
        whRef,
        where('staffId', '==', staffId),
        where('salonId', '==', salonId)
      );
      const whSnapshot = await getDocs(whQuery);

      if (whSnapshot.empty) {
        return { employmentType: 'fulltime', roster: {} };
      }

      const data = whSnapshot.docs[0].data();
      const rawOffDay = data.offDay;
      return {
        employmentType: data.employmentType || 'fulltime',
        roster: data.roster || {},
        offDays: Array.isArray(rawOffDay) ? rawOffDay : (rawOffDay ? [rawOffDay] : []),
      };
    } catch (error) {
      console.error('Error fetching staff working hours:', error);
      return { employmentType: 'fulltime', roster: {}, offDays: [] };
    }
  };

  const checkWorkingHoursAvailability = (staffWH, dateString, timeString) => {
    // Check off days for all employment types
    if (staffWH?.offDays?.length > 0) {
      const dayName = new Date(dateString + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' });
      if (staffWH.offDays.includes(dayName)) {
        return { available: false, reason: 'Staff off day' };
      }
    }

    // Full-time or no document = always available during business hours
    if (!staffWH || staffWH.employmentType === 'fulltime') {
      return { available: true, reason: null };
    }

    // Part-time/contract - check roster
    const daySlots = staffWH.roster[dateString];
    if (!daySlots || !Array.isArray(daySlots) || daySlots.length === 0) {
      return { available: false, reason: 'Outside staff working hours' };
    }

    if (!daySlots.includes(timeString)) {
      return { available: false, reason: 'Outside staff working hours' };
    }

    return { available: true, reason: null };
  };

  const getExistingAppointments = async (staffId, date) => {
    try {
      const appointmentsRef = collection(db, 'appointments');
      const appointmentsQuery = query(
        appointmentsRef,
        where('staffId', '==', staffId),
        where('appointmentDate', '==', date),
        where('status', '==', 'approved')
      );
      const appointmentsSnapshot = await getDocs(appointmentsQuery);

      return appointmentsSnapshot.docs.map(doc => doc.data());
    } catch (error) {
      console.error('Error getting existing appointments:', error);
      return [];
    }
  };

  const timeStringToMinutes = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const formatTimeDisplay = (time24) => {
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const hideStaffSelection = salonId === 'demo-clinic';

  const validateForm = () => {
    const errors = {};

    if (isMultiLocation && !selectedLocation) {
      errors.location = 'Please select a location';
    }

    if (!selectedService && !selectedPackageId) {
      errors.service = 'Please select a service or package';
    }

    if (!hideStaffSelection && !selectedStaff) {
      errors.staff = 'Please select a staff member';
    }

    if (!selectedDate) {
      errors.date = 'Please select a date';
    } else {
      // Check if date is in the future
      const selectedDateTime = new Date(selectedDate + 'T' + (selectedTime || '00:00'));
      const now = new Date();
      if (selectedDateTime <= now) {
        errors.date = 'Please select a future date and time';
      }
    }

    if (!selectedTime) {
      errors.time = 'Please select a time';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const calculateEndTime = (startTime, duration) => {
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const startInMinutes = startHours * 60 + startMinutes;
    const endInMinutes = startInMinutes + duration;
    
    const endHours = Math.floor(endInMinutes / 60);
    const endMinutesRemainder = endInMinutes % 60;
    
    return `${endHours.toString().padStart(2, '0')}:${endMinutesRemainder.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setError('Please fill in all required fields correctly.');
      return;
    }

    // Closure guard — re-validate at submission time in case closures changed
    // after the date picker was used.
    const holidayMatch = holidayClosures.find(h => h && h.date === selectedDate);
    if (holidayMatch) {
      setError(
        `The salon is closed on ${new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-SG', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} ` +
        `for ${holidayMatch.name || 'a Public Holiday'}. Please select a different date.`
      );
      return;
    }
    const selDateObj = new Date(selectedDate + 'T00:00:00');
    selDateObj.setHours(0, 0, 0, 0);
    const customMatch = customClosures.find(c => {
      if (!c || !c.startDate || !c.endDate) return false;
      const start = new Date(c.startDate + 'T00:00:00'); start.setHours(0, 0, 0, 0);
      const end   = new Date(c.endDate   + 'T00:00:00'); end.setHours(23, 59, 59, 999);
      return selDateObj >= start && selDateObj <= end;
    });
    if (customMatch) {
      setError(
        `The salon is closed on ${new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-SG', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} ` +
        `(${customMatch.reason || 'Custom Closure'}). Please select a different date.`
      );
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const selectedServiceData = allServices.find(s => s.id === selectedService);
      const selectedStaffData = hideStaffSelection ? null : allStaff.find(s => s.id === selectedStaff);
      const selectedPackageData = selectedPackageId
        ? customerActivePackages.find(p => p.id === selectedPackageId)
        : null;

      if (!selectedServiceData && !selectedPackageData) {
        throw new Error('Selected service or package not found');
      }

      if (!hideStaffSelection && !selectedStaffData) {
        throw new Error('Selected staff member not found');
      }

      // Create appointment date/time
      const appointmentDateTime = new Date(selectedDate + 'T' + selectedTime);

      // Use service duration if available, otherwise fall back to package duration or 60 min
      const appointmentDuration = selectedServiceData?.duration || selectedPackageData?.sessionDuration || 60;

      // Calculate end time based on service/package duration
      const endTime = calculateEndTime(selectedTime, appointmentDuration);

      // Generate unique IDs
      const appointmentId = `apt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const bookingId = `${Date.now()}${Math.random().toString(36).substr(2, 9)}`;

      // Create appointment data for main appointments collection
      const appointmentData = {
        appointmentId: appointmentId,
        bookingId: bookingId,
        appointmentDate: selectedDate,
        startTime: selectedTime,
        endTime: endTime,
        customerId: `${currentUser.email}_${salonId}`,
        customerEmail: currentUser.email,
        customerName: currentUser.displayName || currentUser.email,
        staffId: selectedStaffData?.id || null,
        staffName: selectedStaffData?.name || null,
        serviceId: selectedServiceData?.id || null,
        serviceRequested: selectedServiceData
          ? {
              id: selectedServiceData.id,
              name: selectedServiceData.name,
              duration: selectedServiceData.duration || 60,
              price: selectedServiceData.price || 0
            }
          : null,
        dateTimeRequested: Timestamp.fromDate(appointmentDateTime),
        dateTimeCreated: Timestamp.fromDate(new Date()),
        status: salonConfig?.autoApproveBookings ? 'approved' : 'pending',
        notes: notes.trim(),
        salonId: salonId,
        locationId: selectedLocation || '',
        clientId: selectedServiceData?.clientId || salonConfig?.clientId || 'default-client',
        ...(selectedPackageId ? { packageId: selectedPackageId } : {})
      };

      // If this is a reschedule, cancel the old appointment first
      if (isReschedule && rescheduleData && rescheduleData.originalAppointmentId) {
        try {
          console.log('Cancelling old appointment for reschedule...');
          console.log('Original appointment document ID:', rescheduleData.originalAppointmentId);

          const oldAppointmentRef = doc(db, 'appointments', rescheduleData.originalAppointmentId);

          const oldAppointmentDoc = await getDoc(oldAppointmentRef);
          if (oldAppointmentDoc.exists()) {
            console.log('Found old appointment document, cancelling...');
            await updateDoc(oldAppointmentRef, {
              status: 'cancelled',
              cancelledAt: new Date(),
              cancelReason: 'Rescheduled',
              cancelledBy: 'customer-reschedule',
              updatedAt: new Date()
            });
            console.log('Successfully cancelled old appointment');
          } else {
            console.warn('Could not find old appointment to cancel with ID:', rescheduleData.originalAppointmentId);
          }

        } catch (cancelError) {
          console.error('Error cancelling old appointment:', cancelError);
          // Continue with new appointment creation even if old cancellation fails
        }
      }

      console.log('Creating new appointment with data:', appointmentData);

      // Save to main appointments collection
      const appointmentsRef = collection(db, 'appointments');
      await addDoc(appointmentsRef, appointmentData);
      
      console.log('Appointment saved successfully to main appointments collection');

      setSuccessMessage(
        isReschedule
          ? salonConfig?.autoApproveBookings
            ? 'Your appointment has been rescheduled and confirmed!'
            : 'Your appointment has been rescheduled successfully! The salon will review and confirm your new appointment time.'
          : salonConfig?.autoApproveBookings
            ? 'Your appointment is confirmed!'
            : 'Your appointment request has been submitted successfully! The salon will review and confirm your booking.'
      );

      // Clear form
      setSelectedService('');
      setSelectedStaff('');
      setSelectedDate('');
      setSelectedTime('');
      setNotes('');
      setFormErrors({});

      // Redirect to dashboard after a delay
      setTimeout(() => {
        navigate(`/BespokeBeauty/${salonId}/customer/home`);
      }, 3000);

    } catch (error) {
      console.error('Error submitting appointment:', error);
      setError('Failed to submit appointment request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/BespokeBeauty/${salonId}/customer/home`);
  };

  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Get maximum date (3 months from now)
  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 3);
    return maxDate.toISOString().split('T')[0];
  };

  // #Revised UI_v2 — CE3: determine which dates are bookable for the datepicker
  const isDateAvailable = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return false;

    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
    const dayHours = businessHours.find(d => d.day === dayName);
    if (!dayHours || dayHours.isClosed) return false;

    const dateStr = [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0'),
    ].join('-');

    if (holidayClosures.some(h => h && h.date === dateStr)) return false;

    const dateAtMidnight = new Date(date);
    dateAtMidnight.setHours(0, 0, 0, 0);
    if (customClosures.some(closure => {
      if (!closure?.startDate || !closure?.endDate || !closure.isFullDay) return false;
      const start = new Date(closure.startDate); start.setHours(0, 0, 0, 0);
      const end   = new Date(closure.endDate);   end.setHours(23, 59, 59, 999);
      return dateAtMidnight >= start && dateAtMidnight <= end;
    })) return false;

    return true;
  };

  // Step progress bar active step
  const currentStep = (isMultiLocation && !selectedLocation)
    ? 0
    : !selectedService ? 1 : !selectedStaff ? 2 : !selectedDate || !selectedTime ? 3 : 4;

  if (loading) {
    return (
      <div className="booking-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading booking information...</p>
        </div>
      </div>
    );
  }

  // Step definitions
  const BOOKING_STEPS = [
    ...(isMultiLocation ? [{ step: 0, label: 'Choose Location' }] : []),
    { step: 1, label: 'Select Service' },
    { step: 2, label: 'Choose Staff' },
    { step: 3, label: 'Pick Time' },
    { step: 4, label: 'Confirm' },
  ];

  return (
    <div className="booking-container">
      {/* #Revised UI_v2 — CE3: horizontal step progress bar */}
      <div className="booking-step-bar">
        {BOOKING_STEPS.map(({ step, label }, idx) => {
          const state = currentStep > step ? 'completed' : currentStep === step ? 'active' : 'pending';
          return (
            <React.Fragment key={step}>
              <div className={`booking-step booking-step--${state}`}>
                <div className="booking-step-circle">
                  {state === 'completed' ? '✓' : step}
                </div>
                <div className="booking-step-label">{label}</div>
              </div>
              {idx < BOOKING_STEPS.length - 1 && (
                <div className={`booking-step-connector ${state === 'completed' ? 'completed' : ''}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      <div className="booking-header">
        <h1>{isReschedule ? 'Reschedule Appointment' : 'Book New Appointment'}</h1>
        {isReschedule && originalAppointment && (
          <div className="reschedule-info">
            <p><strong>Original Appointment:</strong></p>
            <p>{originalAppointment.serviceRequested?.name} with {originalAppointment.staffName}</p>
            <p>{originalAppointment.dateTimeRequested?.toLocaleString()}</p>
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">
          <span className="message-icon">⚠ </span>
          {error}
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          <span className="message-icon">✅</span>
          {successMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="booking-form">
        {/* Location selection — only shown for multi-location salons */}
        {isMultiLocation && (
          <div className="form-section">
            <h3>Choose Location</h3>
            <div className="booking-location-pills">
              {locations.map(loc => (
                <button
                  key={loc.locationId}
                  type="button"
                  className={`booking-loc-pill-customer${selectedLocation === loc.locationId ? ' booking-loc-pill-customer--active' : ''}`}
                  onClick={() => {
                    setSelectedLocation(loc.locationId);
                    setSelectedService('');
                    setSelectedStaff('');
                    setSelectedDate('');
                    setSelectedTime('');
                  }}
                >
                  {loc.locationName}
                </button>
              ))}
            </div>
            {formErrors.location && <span className="error-text">{formErrors.location}</span>}
          </div>
        )}

        <div className="form-section">
          <h3>Service Selection</h3>

          <div className="form-group">
            <label htmlFor="service">Select Service *</label>
            <select
              id="service"
              value={selectedService}
              onChange={(e) => setSelectedService(e.target.value)}
              className={formErrors.service ? 'error' : ''}
              disabled={isMultiLocation && !selectedLocation}
              required
            >
              <option value="">{isMultiLocation && !selectedLocation ? 'Choose a location first…' : 'Choose a service...'}</option>
              {locationFilteredServices.map(service => {
                const priceDisplay = service.priceMin !== undefined
                  ? (service.priceMin === service.priceMax
                      ? `$${Number(service.priceMin).toFixed(2)}`
                      : `$${Number(service.priceMin).toFixed(2)} – $${Number(service.priceMax).toFixed(2)}`)
                  : (service.price ? `$${service.price}` : 'TBD');
                return (
                  <option key={service.id} value={service.id}>
                    {service.name} - {service.duration || 60}min - {priceDisplay}
                  </option>
                );
              })}
            </select>
            {formErrors.service && <span className="error-text">{formErrors.service}</span>}
          </div>

          {selectedService && (
            <div className="service-details">
              {(() => {
                const service = locationFilteredServices.find(s => s.id === selectedService);
                return service ? (
                  <div className="service-info">
                    <h4>{service.name}</h4>
                    <p>{service.description || 'Professional service'}</p>
                    <p><strong>Duration:</strong> {service.duration || 60} minutes</p>
                    <p><strong>Price:</strong> {service.priceMin !== undefined
                      ? (service.priceMin === service.priceMax
                          ? `$${Number(service.priceMin).toFixed(2)}`
                          : `$${Number(service.priceMin).toFixed(2)} – $${Number(service.priceMax).toFixed(2)}`)
                      : (service.price ? `$${service.price}` : 'To be determined')}</p>
                    <p><strong>Category:</strong> {service.category || 'General'}</p>
                  </div>
                ) : null;
              })()}
            </div>
          )}
        </div>

        {/* Package Selection - only shown when customer has active packages */}
        {customerActivePackages.length > 0 && (
          <div className="form-section">
            <h3>Package Selection</h3>
            <p style={{ fontSize: '13px', color: '#6b7280', marginTop: 0 }}>
              Select a package to link this booking to one of your active packages.
            </p>
            <div className="form-group">
              <label htmlFor="package">Select Package (optional)</label>
              <select
                id="package"
                value={selectedPackageId}
                onChange={e => setSelectedPackageId(e.target.value)}
              >
                <option value="">No package — book as a regular appointment</option>
                {customerActivePackages.map(pkg => {
                  const left = Math.max(0, (pkg.totalSessions || 0) - (pkg.sessionsUsed || 0));
                  return (
                    <option key={pkg.id} value={pkg.id} disabled={left === 0}>
                      {pkg.packageName} — {left} session{left !== 1 ? 's' : ''} remaining
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        )}

        {!hideStaffSelection && (
          <div className="form-section">
            <h3>Staff Selection</h3>

            <div className="form-group">
              <label htmlFor="staff">Select Staff Member *</label>
              <select
                id="staff"
                value={selectedStaff}
                onChange={(e) => setSelectedStaff(e.target.value)}
                className={formErrors.staff ? 'error' : ''}
                disabled={!selectedService && !selectedPackageId}
                required
              >
                <option value="">
                  {!selectedService && !selectedPackageId ? 'Please select a service or package first...' : 'Choose a staff member...'}
                </option>
                {filteredStaff.map(staff => (
                  <option key={staff.id} value={staff.id}>
                    {staff.name} {staff.title ? `- ${staff.title}` : ''}
                  </option>
                ))}
              </select>
              {formErrors.staff && <span className="error-text">{formErrors.staff}</span>}
              {selectedService && filteredStaff.length === 0 && (
                <span className="info-text">No staff available for selected service</span>
              )}
            </div>
          </div>
        )}

        <div className="form-section">
          <h3>Date & Time Selection</h3>
          
          <div className="form-row">
            {/* #Revised UI_v2 — CE3: react-datepicker mini-calendar */}
            <div className="form-group">
              <label htmlFor="date">Select Date *</label>
              <DatePicker
                id="date"
                selected={selectedDate ? new Date(selectedDate + 'T12:00:00') : null}
                onChange={(date) => {
                  if (date) {
                    const y = date.getFullYear();
                    const m = String(date.getMonth() + 1).padStart(2, '0');
                    const d = String(date.getDate()).padStart(2, '0');
                    setSelectedDate(`${y}-${m}-${d}`);
                  } else {
                    setSelectedDate('');
                  }
                }}
                minDate={new Date()}
                maxDate={(() => { const d = new Date(); d.setMonth(d.getMonth() + 3); return d; })()}
                filterDate={businessHours.length > 0 ? isDateAvailable : undefined}
                disabled={!selectedStaff}
                dateFormat="dd MMM yyyy"
                placeholderText={!selectedStaff ? 'Select staff first…' : 'Select a date…'}
                className={`booking-datepicker-input${formErrors.date ? ' error' : ''}`}
                calendarClassName="booking-datepicker-calendar"
                showPopperArrow={false}
                autoComplete="off"
              />
              {formErrors.date && <span className="error-text">{formErrors.date}</span>}
            </div>

            {/* #Revised UI_v2 — CE3: time slot pill grid */}
            <div className="form-group">
              <label>Select Time *</label>
              {!selectedDate || !selectedStaff ? (
                <p className="info-text slot-placeholder">Select date and staff member first…</p>
              ) : availableTimeSlots.length === 0 ? (
                <p className="info-text">No available times for selected date</p>
              ) : (
                <>
                  <div className="time-slot-grid">
                    {availableTimeSlots.map(slot => (
                      <button
                        key={slot.value}
                        type="button"
                        className={`time-slot-pill ${
                          !slot.available ? 'unavailable'
                          : selectedTime === slot.value ? 'selected'
                          : 'available'
                        }`}
                        onClick={() => slot.available && setSelectedTime(slot.value)}
                        disabled={!slot.available}
                        title={!slot.available ? slot.reason : undefined}
                      >
                        {slot.label}
                      </button>
                    ))}
                  </div>
                  <small className="info-text">
                    {availableTimeSlots.filter(s => s.available).length} of {availableTimeSlots.length} slots available
                  </small>
                </>
              )}
              {formErrors.time && <span className="error-text">{formErrors.time}</span>}
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Additional Information</h3>
          
          <div className="form-group">
            <label htmlFor="notes">Special Requests or Notes</label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="Any special requests, preferences, or notes for your staff member..."
              maxLength={500}
            />
            <small className="char-count">{notes.length}/500 characters</small>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleCancel}
            className="cancel-button"
            disabled={submitting}
          >
            Cancel
          </button>
          
          <button
            type="submit"
            className="submit-button"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="spinner small"></span>
                {isReschedule ? 'Rescheduling...' : 'Submitting...'}
              </>
            ) : (
              isReschedule ? 'Reschedule Appointment' : 'Submit Booking Request'
            )}
          </button>
        </div>
      </form>

      <div className="booking-info">
        <h3>Important Information</h3>
        <ul>
          <li>All appointments are subject to salon approval and confirmation</li>
          <li>You will receive notification once your appointment is confirmed</li>
          <li>Cancellations or changes must be made at least 24 hours in advance</li>
          <li>Please arrive 10 minutes early for your appointment</li>
          {isReschedule && (
            <li><strong>Your original appointment will be cancelled once this reschedule request is processed</strong></li>
          )}
        </ul>
      </div>
    </div>
  );
}

export default BookingAppointment;